import {
  parseJsonEventStream,
  uiMessageChunkSchema,
  type ChatTransport,
  type UIMessageChunk,
} from "ai";
import { z } from "zod";
import { apiClient, type Api } from "../../services/api";
import {
  uiMessageCards,
  uiMessageText,
  type GeneralChatUIMessage,
} from "../gpt/hooks/useGptChat";

export type AssistantOrigin =
  Api["post"]["/assistant/conversations/:conversationId/messages"]["input"]["json"]["origin"];

export const assistantOriginSchema = z.enum([
  "typed",
  "landing_suggestion",
  "followup_suggestion",
] as const satisfies readonly AssistantOrigin[]);
// `sendMessage`'s body reaches the transport untyped.
const sendBodySchema = z.object({ origin: assistantOriginSchema });
// The hook mints `requestId`, so a replayed regeneration stays idempotent.
const regenerateBodySchema = z.object({ runId: z.uuid(), requestId: z.uuid() });
// The hook resumes the run the history names.
const resumeBodySchema = z.object({ runId: z.uuid() });

type ChunkResult =
  ReturnType<
    typeof parseJsonEventStream<UIMessageChunk>
  > extends ReadableStream<infer Result>
    ? Result
    : never;

type ActiveStream = { runId: string; controller: AbortController };

// What the server pass found in a Question or a card answer (AD-20).
export type ServerRedaction =
  Api["post"]["/assistant/conversations/:conversationId/messages"]["output"]["body"]["redaction"];

// An API from before the server pass sends no `redaction` (a rollback): absent means no server pass.
const serverRedactionSchema = z
  .object({
    text: z.string(),
    summary: z
      .object({
        matchCount: z.number(),
        categories: z.array(z.enum(["patient_name", "contact"])),
      })
      .nullable(),
  })
  .nullable()
  .optional();

// The 503 of a send that the server could not check (AD-20), read off the request error.
const redactionRefusalSchema = z.object({
  responseStatus: z.literal(503),
  responseBody: z.object({ code: z.literal("redaction_unavailable") }),
});
// A cancel that finds the run already ended.
const cancelConflictSchema = z.object({ responseStatus: z.literal(409) });

function serverRedactionOf(field: unknown): ServerRedaction | null {
  const parsed = serverRedactionSchema.parse(field);
  return parsed === undefined ? null : parsed;
}

// The AI SDK's `stop()` aborts only the requests it started, so a resumed stream
// and the run itself are stopped here. A failed request rejects to `useChat`,
// which shows it on the turn.
export function createAssistantChatTransport(input: {
  conversationId: string;
  // Null for a regeneration, which sends no new text.
  onAccepted: (redaction: ServerRedaction | null) => void;
  // The server moved the question to a new conversation, which holds its run.
  onMoved: (conversationId: string) => void;
}) {
  const conversationId = input.conversationId;
  let active: ActiveStream | null = null;
  // The run whose stream ended on an `error` chunk.
  let failedRunId: string | null = null;
  // The server could not check the text for patient data, so it stored nothing (AD-20).
  let redactionRefused = false;

  function cancel(runConversationId: string, runId: string): Promise<void> {
    return apiClient
      .post("/assistant/conversations/:conversationId/runs/:runId/commands", {
        param: { conversationId: runConversationId, runId },
        json: { kind: "cancel", commandId: crypto.randomUUID() },
      })
      .then(
        () => undefined,
        (error: unknown) => {
          // A fresh command conflicts only with a run that has already ended.
          if (cancelConflictSchema.safeParse(error).success) return;
          throw error;
        }
      );
  }

  // `from` continues a message the reader holds; null replays the whole run.
  function open(entry: ActiveStream, from: number | null) {
    active = entry;
    return apiClient
      .get("/assistant/conversations/:conversationId/runs/:runId/stream", {
        param: { conversationId, runId: entry.runId },
        query: from === null ? {} : { from },
        signal: entry.controller.signal,
        stream: true,
      })
      .then((response) =>
        parseJsonEventStream({
          // `stream: true` hands back the raw body, which the route's plain `Response` types as `{}`.
          stream: z.instanceof(ReadableStream<Uint8Array>).parse(response.body),
          schema: uiMessageChunkSchema,
        }).pipeThrough(
          new TransformStream<ChunkResult, UIMessageChunk>({
            transform(chunk, controller) {
              if (!chunk.success) throw chunk.error;
              if (chunk.value.type === "error") failedRunId = entry.runId;
              controller.enqueue(chunk.value);
            },
            flush() {
              if (active === entry) active = null;
            },
          })
        )
      );
  }

  async function accept(
    messages: GeneralChatUIMessage[],
    trigger: "submit-message" | "regenerate-message",
    body: object | undefined
  ): Promise<
    | {
        kind: "here";
        runId: string;
        from: number | null;
        redaction: ServerRedaction | null;
      }
    | {
        kind: "moved";
        conversationId: string;
        runId: string;
        redaction: ServerRedaction | null;
      }
  > {
    if (trigger === "regenerate-message") {
      const { runId, requestId } = regenerateBodySchema.parse(body);
      return apiClient
        .post(
          "/assistant/conversations/:conversationId/runs/:runId/regenerate",
          { param: { conversationId, runId }, json: { requestId } }
        )
        .then((response) => ({
          kind: "here",
          runId: response.body.runId,
          from: null,
          redaction: null,
        }));
    }
    const last = messages.at(-1);
    if (last === undefined)
      throw new Error("should not happen: a send without its question");
    // An answered card resumes its run in the same message. The card's id
    // names the command, so a replay after a lost connection is idempotent.
    if (last.role === "assistant") {
      const card = uiMessageCards(last).at(-1);
      if (card === undefined || card.answer === null)
        throw new Error("should not happen: a continuation without its answer");
      if (last.metadata === undefined || last.metadata.runId === null)
        throw new Error("should not happen: a card without its run");
      const runId = last.metadata.runId;
      return apiClient
        .post("/assistant/conversations/:conversationId/runs/:runId/commands", {
          param: { conversationId, runId },
          json: {
            kind: "clarify",
            commandId: card.toolCallId,
            requestId: card.toolCallId,
            text: card.answer,
          },
        })
        .then((response) => ({
          kind: "here",
          runId: response.body.runId,
          from: response.body.eventCursor,
          redaction: serverRedactionOf(response.body.redaction),
        }));
    }
    return apiClient
      .post("/assistant/conversations/:conversationId/messages", {
        param: { conversationId },
        json: {
          requestId: last.id,
          text: uiMessageText(last),
          origin: sendBodySchema.parse(body).origin,
          onSplit: "follow",
        },
      })
      .then((response) =>
        // The server spells a UUID in lowercase; a typed URL may not.
        response.body.conversationId === conversationId.toLowerCase()
          ? {
              kind: "here",
              runId: response.body.runId,
              from: null,
              redaction: serverRedactionOf(response.body.redaction),
            }
          : {
              kind: "moved",
              conversationId: response.body.conversationId,
              runId: response.body.runId,
              redaction: serverRedactionOf(response.body.redaction),
            }
      );
  }

  const transport: ChatTransport<GeneralChatUIMessage> = {
    async sendMessages({ messages, trigger, body, abortSignal }) {
      active = null;
      failedRunId = null;
      redactionRefused = false;
      // The POST is not aborted: once sent, the run exists and only a cancel stops it.
      const accepted = await accept(messages, trigger, body).catch(
        (error: unknown) => {
          redactionRefused = redactionRefusalSchema.safeParse(error).success;
          throw error;
        }
      );
      input.onAccepted(accepted.redaction);
      const stopped = abortSignal !== undefined && abortSignal.aborted;
      switch (accepted.kind) {
        case "here": {
          if (stopped) {
            await cancel(conversationId, accepted.runId);
            throw new DOMException("Stopped", "AbortError");
          }
          const entry: ActiveStream = {
            runId: accepted.runId,
            controller: new AbortController(),
          };
          if (abortSignal !== undefined)
            abortSignal.addEventListener("abort", () => {
              entry.controller.abort();
            });
          return open(entry, accepted.from);
        }
        case "moved":
          // The new conversation holds the question: its page resumes the run, or shows it stopped.
          try {
            if (stopped) await cancel(accepted.conversationId, accepted.runId);
          } finally {
            // The question is in B even when its cancel fails.
            input.onMoved(accepted.conversationId);
          }
          throw new DOMException("Stopped", "AbortError");
        default:
          throw new Error(accepted satisfies never);
      }
    },
    async reconnectToStream({ body }) {
      failedRunId = null;
      return open(
        {
          runId: resumeBodySchema.parse(body).runId,
          controller: new AbortController(),
        },
        null
      );
    },
  };

  return {
    transport,
    // Null when the turn broke before its run could fail: the request was
    // refused, or the connection was lost.
    failedRunId() {
      return failedRunId;
    },
    // True when the last send failed because the server pass was unavailable.
    redactionRefused() {
      return redactionRefused;
    },
    async stop() {
      const current = active;
      active = null;
      if (current === null) return;
      current.controller.abort();
      await cancel(conversationId, current.runId);
    },
  };
}
